import { useEffect, useMemo, useRef, useState } from 'react';
import { Board } from '@/chess/board';
import { KidHint, TurnIndicator } from '@/ui';
import type { DrillPosition, RepertoireDetail } from './types';
import { openingLabApi, type AttemptPayload } from './api';

interface Props {
  repertoire: RepertoireDetail;
  /** Liste des positions critiques (is_critical + plan défini). */
  critical: DrillPosition[];
  /** Pool de plans pour générer des distracteurs. */
  allPlans: string[];
  onResult: (result: 'success' | 'partial' | 'fail') => void;
}

/**
 * Mode `plan` (QCM) : à une position critique, propose 3 plans textuels (1 vrai
 * + 2 distracteurs tirés des autres `#plan` du même répertoire). Force la
 * compréhension stratégique vs la mémoire de coups.
 */
export function DrillPlan({ repertoire, critical, allPlans, onResult }: Props) {
  const [position, setPosition] = useState(() => critical[0] ?? null);
  const [phase, setPhase] = useState<'playing' | 'revealed'>('playing');
  const [picked, setPicked] = useState<number | null>(null);
  const startedAtRef = useRef<number>(performance.now());

  useEffect(() => {
    startedAtRef.current = performance.now();
    setPhase('playing');
    setPicked(null);
  }, [position]);

  const choices = useMemo(() => {
    if (!position?.tags.plan) return [];
    const truth = position.tags.plan;
    const distractorsPool = allPlans.filter((p) => p !== truth);
    const distractors = sample(distractorsPool, 2);
    const all = [truth, ...distractors];
    return shuffle(all).map((text, idx) => ({ text, isCorrect: text === truth, idx }));
  }, [position, allPlans]);

  if (!position) {
    return (
      <p className="rounded-xl border border-ink-700 bg-ink-800 p-6 text-body text-ink-300">
        Aucune position critique dans ce répertoire. Préviens Valentin pour qu'il en ajoute.
      </p>
    );
  }

  // MVP2.3 T6 — Si le répertoire n'a pas assez de plans pour 3 choix QCM
  // (pool de distracteurs < 2), bascule en mode "découverte" : affiche le
  // plan correct révélé directement, sans QCM. Léon apprend quand même, et
  // on ne pollue PAS les KPIs avec un faux "success" artificiel (skip
  // logAttempt en discovery — un drill non-noté reste pédagogique).
  const isDiscoveryMode = choices.length < 3 && position.tags.plan !== undefined;

  function logAttempt(result: AttemptPayload['result']) {
    const payload: AttemptPayload = {
      module: 'opening',
      slug: `${repertoire.slug}:plan-ply${position!.ply}`,
      fen: position!.fenBefore,
      side_to_move: position!.sideToMove,
      mode: 'plan',
      result,
      time_spent_ms: Math.round(performance.now() - startedAtRef.current),
      source_ref: repertoire.slug,
    };
    openingLabApi.postAttempt(payload).catch((e) => console.error('[plan] log failed', e));
    onResult(result);
  }

  function pick(idx: number) {
    if (phase === 'revealed') return;
    setPicked(idx);
    setPhase('revealed');
    const correct = choices[idx]?.isCorrect ?? false;
    logAttempt(correct ? 'success' : 'fail');
  }

  function next() {
    const remaining = critical.filter((p) => p.ply !== position!.ply);
    setPosition(remaining[Math.floor(Math.random() * Math.max(remaining.length, 1))] ?? critical[0] ?? null);
  }

  return (
    <div className="flex flex-col gap-4">
      <KidHint tone="violet" title="Pourquoi ce drill ?">
        Mémoriser un coup c'est bien, comprendre POURQUOI on le joue c'est
        mieux. Tu choisis le bon plan parmi trois — celui qui correspond à
        l'idée stratégique de l'ouverture.
      </KidHint>
      <header className="flex items-baseline justify-between">
        <p className="text-caption uppercase text-violet-500">Plan · coup {position.ply} · {position.sideToMove === 'white' ? 'Blancs' : 'Noirs'}</p>
        <p className="text-body-sm text-ink-300">
          {isDiscoveryMode ? 'Pas assez de plans pour un QCM. Lis et mémorise.' : 'Quel plan poursuivre ?'}
        </p>
      </header>

      <TurnIndicator
        state={phase === 'playing' ? 'user-turn' : 'idle'}
        userLabel="À toi de choisir le bon plan"
        idleLabel="Plan révélé"
      />

      <Board
        fen={position.fenBefore}
        orientation={repertoire.color}
        interactive={false}
        turnColor={position.sideToMove}
      />

      {isDiscoveryMode ? (
        <section
          aria-label="Plan révélé (mode découverte)"
          className="rounded-lg border border-violet-500 bg-violet-500/10 p-4 text-body text-paper-50"
        >
          {position.tags.plan}
        </section>
      ) : (
        <section className="flex flex-col gap-2">
          {choices.map((c, i) => (
            <button
              key={c.idx}
              type="button"
              onClick={() => pick(i)}
              disabled={phase === 'revealed'}
              className={
                'rounded-lg border px-4 py-3 text-left text-body transition ' +
                (phase === 'revealed' && c.isCorrect
                  ? 'border-volt-500 bg-volt-500/10 text-volt-500'
                  : phase === 'revealed' && picked === i
                  ? 'border-coral-500 bg-coral-500/10 text-coral-500'
                  : 'border-ink-700 bg-ink-800 text-paper-50 hover:border-violet-500')
              }
            >
              {c.text}
            </button>
          ))}
        </section>
      )}

      {(isDiscoveryMode || phase === 'revealed') && (
        <button
          type="button"
          onClick={next}
          className="self-start rounded-lg bg-violet-500 px-4 py-2 font-display text-body text-paper-50 transition hover:opacity-90"
        >
          Position suivante
        </button>
      )}
    </div>
  );
}

function sample<T>(arr: T[], n: number): T[] {
  const copy = [...arr];
  const out: T[] = [];
  while (copy.length > 0 && out.length < n) {
    const i = Math.floor(Math.random() * copy.length);
    out.push(copy.splice(i, 1)[0]!);
  }
  return out;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}
